import { Device, requestFullScreen } from '@goodboydigital/astro';
import sha256 from 'crypto-js/sha256';
const pako = require('pako');

export class Browser
{
    /** Get all url params */
    public static getUrlParams(): Record<string, string>
    {
        const url = location.search;
        const items = url.slice(url.indexOf('?') + 1).split('&');
        const params = {} as Record<string, any>;

        for (const item of items)
        {
            const split = item.split('=');
            const k = split[0];
            const v = split[1] !== undefined ? split[1] : '';

            params[k] = v;
        }

        return params;
    }

    /**
     * Get a single url param as string
     * @param name - Name of the param
     * @param fallback - Fallback if not found
     * @returns
     */
    public static getUrlString(name: string, fallback: string): string
    {
        const params = this.getUrlParams();

        return params[name] !== undefined ? String(params[name]) : fallback;
    }

    /**
     * Get a single url param as number
     * @param name - Name of the param
     * @param fallback - Fallback if not found
     * @returns
     */
    public static getUrlNumber(name: string, fallback: number): number
    {
        const params = this.getUrlParams();

        return params[name] !== undefined ? Number(params[name]) : fallback;
    }

    /**
     * Get a single url param as boolean - if the flag is set without a value, is considered true
     * @param name - Name of the param
     * @param fallback - Fallback if not found
     * @returns
     */
    public static getUrlBoolean(name: string, fallback: boolean): boolean
    {
        const params = this.getUrlParams();

        if (params[name] === undefined) return fallback;

        const value = params[name].toLowerCase();

        if (value === '') return true;
        if (value === 'false') return false;
        if (value) return true;

        return fallback;
    }

    /** Get browser's navigator language */
    public static getSystemLanguage(dashReplacement = '_'): string
    {
        const lang = this.getUrlString('navigatorLanguage', navigator.language || '');

        return lang.replace('-', dashReplacement);
    }

    /** Wrapper around window.open - in app should open ur in a webview overlay */
    public static open(url: string, target = '_blank'): void
    {
        
        if(window.cordova){
            window.cordova.InAppBrowser.open(url, target, 'location=yes');
        }
        else{
            window.open(url, target);
        }
    }

    /**
     * Get a sha256 hash from sting, using Web crypto API
     * From: https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#converting_a_digest_to_a_hex_string
     * @param text - Text to convert into a hash
     * @returns
     */
    public static async sha256BrowserCrypto(text: string): Promise<string>
    {
        const msgUint8 = new TextEncoder().encode(text); // encode as (utf-8) Uint8Array
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8); // hash the text
        const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
        const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string

        return hashHex;
    }

    /**
     * Get sha256 hash from string using crypto-js, does not depend on browser's crypto API and can work without https
     * @param text - Text to convert into a hash
     * @returns
     */
    public static sha256(text: string): string
    {
        const k = sha256(text);

        return k.toString();
    }

    public static saveJsonFile(name: string, data: string|any): void
    {
        console.log(`[Browser] Save json: ${name}`);

        if (typeof data === 'object') data = JSON.stringify(data, null, 4);

        const type = 'application/json';
        const a = document.createElement('a') as any;
        const file = new Blob([data], { type });

        a.href = URL.createObjectURL(file);
        a.download = name;
        a.click();

        setTimeout(() =>
        {
            window.URL.revokeObjectURL(a.href);
        }, 0);
    }

    public static saveTextFile(name: string, text: string): void
    {
        console.log(`[Browser] Save text file: ${name}`);

        const type = 'application/text';
        const a = document.createElement('a') as any;
        const file = new Blob([text], { type });

        a.href = URL.createObjectURL(file);
        a.download = name;
        a.click();

        setTimeout(() =>
        {
            window.URL.revokeObjectURL(a.href);
        }, 0);
    }

    public static saveBinFile(name: string, bin: ArrayBuffer): void
    {
        console.log(`[Browser] Save text file: ${name}`);

        const type = 'application/text';
        const a = document.createElement('a') as any;
        const file = new Blob([bin], { type });

        a.href = URL.createObjectURL(file);
        a.download = name;
        a.click();

        setTimeout(() =>
        {
            window.URL.revokeObjectURL(a.href);
        }, 0);
    }

    public static async loadJson<T>(url: string): Promise<T | null>
    {
        try
        {
            const response = await fetch(url);
            const json = await response.json();

            return json;
        }
        catch (e)
        {
            console.warn(`Failed to fetch json file: ${url}`);

            return null;
        }
    }

    public static async loadJsz<T>(url: string): Promise<T>
    {
        const response = await fetch(url);
        const buffer = await response.arrayBuffer();
        const json = JSON.parse(pako.inflate(buffer, { to: 'string' }));

        return json;
    }

    public static enterFullScreen(orientation: OrientationLockType = 'landscape'): void
    {
        if (!Device.desktop) requestFullScreen();
        setTimeout(() => this.lockOrientation(orientation), 200);
    }

    public static exitFullScreen(): void
    {
        if (document.exitFullscreen && document.fullscreenElement)
        {
            document.exitFullscreen();
        }
    }

    public static async lockOrientation(orientation: OrientationLockType = 'landscape'): Promise<void>
    {
        if (!screen.orientation) return;
        await screen.orientation.lock(orientation).catch(() =>
        {
            // This error can be ignored
            // console.warn(e);
        });
    }

    public static unlockOrientation(): void
    {
        if (!screen.orientation || !screen.orientation.unlock) return;
        screen.orientation.unlock();
    }
}
